
import { useRef, useState, useEffect, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { orpc } from "@/frontend/orpc-client";
import { Button } from "@/frontend/components/ui/button";
import { OnboardingLayout } from "@/frontend/components/onboarding";
import type { SignScreen } from "@/frontend/types/sign";
import {
  FileSignature,
  Camera,
  AlertCircle,
  ArrowLeft,
  RotateCcw,
  Send,
  Smartphone,
  Loader2,
  Focus,
  Sun,
  Hand,
} from "lucide-react";

import {useIsMobileUA} from "@/frontend/hooks/use-mobile"


// ── Config ────────────────────────────────────────────────────────────────────
const GUIDE_INSET_X = 0.06;
const GUIDE_INSET_Y = 0.10;


function drawCorners(x: number, y: number, w: number, h: number, ctx: CanvasRenderingContext2D) {
  const len = 22;
  ctx.lineWidth = 4;
  ctx.lineCap   = "round";
  const corners: [number, number, number, number][] = [
    [x,     y,      1,  1],
    [x + w, y,     -1,  1],
    [x,     y + h,  1, -1],
    [x + w, y + h, -1, -1],
  ];
  for (const [cx, cy, sx, sy] of corners) {
    ctx.beginPath();
    ctx.moveTo(cx + sx * len, cy);
    ctx.lineTo(cx, cy);
    ctx.lineTo(cx, cy + sy * len);
    ctx.stroke();
  }
}


// ── Stream cleanup registry ───────────────────────────────────────────────────
// Set when the camera is active; cleared when the stream stops.
let streamCleanup: (() => void) | null = null;
export function stopActiveStream() { streamCleanup?.(); }

// ── Main component ────────────────────────────────────────────────────────────
const ConsentSign = () => {
  // DOM refs
  const videoRef   = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const flashRef   = useRef<HTMLDivElement>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const rafIdRef  = useRef<number>(0);

  const [screen, setScreen]           = useState<SignScreen>("idle");
  const [permDenied, setPermDenied]   = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [capturedUrl, setCapturedUrl] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Submit mutation ──────────────────────────────────────────────────────
  const submitMutation = useMutation(
    orpc.onboarding.completeSignature.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.onboarding.getState.key() });
        navigate({ to: "/" });
      },
    })
  );

  // ── Overlay ─────────────────────────────────────────────────────────────────

  function drawOverlay() {
    const canvas = overlayRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dw = canvas.clientWidth;
    const dh = canvas.clientHeight;
    if (!dw || !dh) return;
    canvas.width  = dw;
    canvas.height = dh;

    const ix = dw * GUIDE_INSET_X;
    const iy = dh * GUIDE_INSET_Y;
    const rw = dw - ix * 2;
    const rh = dh - iy * 2;

    ctx.fillStyle = "rgba(0,0,0,0.45)";
    ctx.fillRect(0, 0, dw, dh);
    ctx.clearRect(ix, iy, rw, rh);

    ctx.strokeStyle = "rgb(79,142,247)";
    ctx.lineWidth   = 3;
    ctx.strokeRect(ix, iy, rw, rh);
    drawCorners(ix, iy, rw, rh, ctx);
  }

  // ── Frame loop ───────────────────────────────────────────────────────────────

  function processFrame() {
    const video = videoRef.current;
    if (!streamRef.current || !video) return;
    rafIdRef.current = requestAnimationFrame(processFrame);
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (vw && vh) drawOverlay();
  }

  // ── Capture ──────────────────────────────────────────────────────────────────

  function captureNow() {
    const video = videoRef.current;
    if (!video) return;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return;

    // Flash
    const flash = flashRef.current;
    if (flash) {
      flash.style.opacity = "1";
      setTimeout(() => { flash.style.opacity = "0"; }, 160);
    }

    // The overlay guide box is drawn in CSS display space.
    // The video uses object-cover, so part of the native frame may be cropped.
    // We must map display-space guide coords → native video pixel coords.
    const displayW = video.clientWidth;
    const displayH = video.clientHeight;

    // object-cover scale: fill the container, crop the overflow
    const scale = Math.max(displayW / vw, displayH / vh);
    const overflowX = (vw * scale - displayW) / 2;
    const overflowY = (vh * scale - displayH) / 2;

    // Guide box in display coordinates
    const guideX = displayW * GUIDE_INSET_X;
    const guideY = displayH * GUIDE_INSET_Y;
    const guideW = displayW * (1 - 2 * GUIDE_INSET_X);
    const guideH = displayH * (1 - 2 * GUIDE_INSET_Y);

    // Map to native video pixel coordinates
    const cropX = Math.round((guideX + overflowX) / scale);
    const cropY = Math.round((guideY + overflowY) / scale);
    const cropW = Math.round(guideW / scale);
    const cropH = Math.round(guideH / scale);

    const crop = document.createElement("canvas");
    crop.width  = cropW;
    crop.height = cropH;
    const cropCtx = crop.getContext("2d")!;
    cropCtx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

    const dataUrl = crop.toDataURL("image/jpeg", 0.85);
    if (!dataUrl || dataUrl === "data:,") {
      setError("Failed to capture image. Please try again.");
      return;
    }

    stopStreamOnly();
    setCapturedUrl(dataUrl);
    setScreen("verify");
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  function stopStreamOnly() {
    cancelAnimationFrame(rafIdRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    streamCleanup = null;
  }

  function resetCapture() {
    setCapturedUrl(null);
  }

  // ── Camera start / stop ────────────────────────────────────────────────────

  async function checkCameraPermission(): Promise<PermissionState | "unknown"> {
    if (!navigator.permissions) return "unknown";
    try {
      const result = await navigator.permissions.query({ name: "camera" as PermissionName });
      return result.state;
    } catch { return "unknown"; }
  }

  async function startCamera() {
    setError(null);
    setPermDenied(false);
    setCameraReady(false);

    if ((await checkCameraPermission()) === "denied") {
      setPermDenied(true);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      streamCleanup = stopStreamOnly;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      resetCapture();
      setScreen("camera");
      rafIdRef.current = requestAnimationFrame(processFrame);

      const onPopState = () => { stopStreamOnly(); setScreen("idle"); window.removeEventListener("popstate", onPopState); };
      window.addEventListener("popstate", onPopState);
    } catch (err) {
      if (err instanceof Error && err.name === "NotAllowedError") setPermDenied(true);
      else setError("Could not access camera. Please check your device and try again.");
    }
  }

  function stopCamera() {
    stopStreamOnly();
    resetCapture();
    setScreen("idle");
  }

  function retake() {
    startCamera();
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const isMobile = useIsMobileUA();

  const selfSignLinkMutation = useMutation(orpc.onboarding.getSelfSignLink.mutationOptions());

  const fetchSignLink = useCallback(() => {
    selfSignLinkMutation.mutate({});
  }, [selfSignLinkMutation.mutate]);

  useEffect(() => {
    if (!isMobile) fetchSignLink();
  }, [isMobile, fetchSignLink]);

  return (
    <OnboardingLayout>
      <div className="flex-1 flex flex-col px-5 py-10">

        {/* Header */}
        <div className="flex flex-row gap-3 items-center mb-5">
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <FileSignature className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight leading-tight">Capture your signature</h1>
            <p className="text-xs text-muted-foreground leading-snug mt-0.5">Sign your name with a pen on a plain white paper, then use your camera to capture it.</p>
          </div>
        </div>

        {/* ── IDLE ── */}
        {screen === "idle" && (
          <>
            {isMobile ? (
              <div className="rounded-xl bg-muted/50 overflow-hidden">
                {/* Simulated camera frame preview */}
                <div className="relative bg-neutral-900 mx-4 mt-4 rounded-lg aspect-video flex items-center justify-center">
                  {/* Corner brackets — match the actual capture overlay */}
                  <div className="absolute top-3 left-3 w-6 h-6 border-t-[3px] border-l-[3px] border-blue-400 rounded-tl-sm" />
                  <div className="absolute top-3 right-3 w-6 h-6 border-t-[3px] border-r-[3px] border-blue-400 rounded-tr-sm" />
                  <div className="absolute bottom-3 left-3 w-6 h-6 border-b-[3px] border-l-[3px] border-blue-400 rounded-bl-sm" />
                  <div className="absolute bottom-3 right-3 w-6 h-6 border-b-[3px] border-r-[3px] border-blue-400 rounded-br-sm" />
                  {/* Signature placeholder */}
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-28 h-0.5 bg-white/30 rounded-full" />
                    <p className="text-[10px] text-white/40 tracking-wide uppercase">your signature</p>
                  </div>
                </div>

                {/* Steps */}
                <div className="p-4 space-y-2.5">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                      <span className="text-[9px] font-bold text-primary">1</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Lay the paper flat on a well-lit surface</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                      <span className="text-[9px] font-bold text-primary">2</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Point the camera down and align your signature inside the blue frame</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                      <span className="text-[9px] font-bold text-primary">3</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Hold steady, then tap <strong>Capture</strong></p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4 p-5 rounded-xl bg-muted/50 text-center">
                <Smartphone className="w-8 h-8 text-primary" />
                <h2 className="text-base font-semibold tracking-tight">Use Your Phone to Sign</h2>
                <p className="text-sm text-muted-foreground">
                  Scan the QR code below with your phone to capture your signature.
                </p>
                {selfSignLinkMutation.isPending && (
                  <div className="w-[180px] h-[180px] flex items-center justify-center bg-white rounded-xl shadow-sm">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                )}
                {selfSignLinkMutation.isError && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    Failed to generate sign link. Please refresh and try again.
                  </div>
                )}
                {selfSignLinkMutation.data?.magicLinkUrl && (
                  <>
                    <div className="p-3 bg-white rounded-xl shadow-sm" data-ph-no-capture>
                      <QRCodeSVG value={selfSignLinkMutation.data.magicLinkUrl} size={180} />
                    </div>
                    <p className="text-xs text-muted-foreground">Link expires {new Date(selfSignLinkMutation.data.expiresAt).toLocaleString()}</p>
                  </>
                )}
              </div>
            )}

            {permDenied && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm mt-3">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                Camera access was denied. Please allow camera access in your browser settings and try again.
              </div>
            )}
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm mt-3">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            {isMobile && (
              <div className="mt-auto pt-5">
                <Button onClick={startCamera} className="w-full h-14 text-base">
                  Open Camera <Camera className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}
          </>
        )}

        {/* ── CAMERA ── always mounted (refs must stay alive) */}
        <div className={screen === "camera" ? "contents" : "hidden"}>
          <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black" data-ph-no-capture>
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-cover"
              playsInline
              muted
              onLoadedMetadata={() => setCameraReady(true)}
            />
            <canvas ref={overlayRef} className="absolute inset-0 w-full h-full pointer-events-none" />
            <div
              ref={flashRef}
              className="absolute inset-0 bg-white pointer-events-none"
              style={{ opacity: 0, transition: "opacity 0.08s ease-out" }}
            />
          </div>

          <div className="grid grid-cols-3 gap-1.5 mt-3">
            <div className="flex flex-col items-center gap-1.5 p-2 rounded-lg bg-muted/50 text-center">
              <Focus className="w-4 h-4 text-primary" />
              <p className="text-[10px] text-muted-foreground leading-tight">Fill the frame with your signature</p>
            </div>
            <div className="flex flex-col items-center gap-1.5 p-2 rounded-lg bg-muted/50 text-center">
              <Sun className="w-4 h-4 text-primary" />
              <p className="text-[10px] text-muted-foreground leading-tight">Use bright, even lighting</p>
            </div>
            <div className="flex flex-col items-center gap-1.5 p-2 rounded-lg bg-muted/50 text-center">
              <Hand className="w-4 h-4 text-primary" />
              <p className="text-[10px] text-muted-foreground leading-tight">Hold steady before capturing</p>
            </div>
          </div>

          <div className="mt-auto pt-5 flex gap-3">
            <Button variant="outline" onClick={stopCamera} className="flex-1 h-14 text-base">
              <ArrowLeft className="w-4 h-4 mr-2" /> Cancel
            </Button>
            <Button onClick={captureNow} disabled={!cameraReady} className="flex-1 h-14 text-base">
              <Camera className="w-5 h-5 mr-2" /> Capture
            </Button>
          </div>
        </div>

        {/* ── VERIFY ── */}
        {screen === "verify" && capturedUrl && (
          <>
            <img
              src={capturedUrl}
              alt="Captured signature"
              className="w-full max-h-[40vh] object-contain rounded-xl border-2 border-emerald-500 bg-black"
              data-ph-no-capture
            />

            {submitMutation.isError && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm mt-3">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                Failed to submit signature. Please try again.
              </div>
            )}

            <div className="mt-auto pt-5 flex gap-3">
              <Button variant="outline" onClick={retake} disabled={submitMutation.isPending} className="flex-1 h-14 text-base">
                <RotateCcw className="w-4 h-4 mr-2" /> Retake
              </Button>
              <Button
                onClick={() => { if (capturedUrl) submitMutation.mutate({ signatureImage: capturedUrl }); }}
                disabled={submitMutation.isPending}
                className="flex-1 h-14 text-base"
              >
                {submitMutation.isPending
                  ? <Loader2 className="w-5 h-5 animate-spin" />
                  : <><Send className="w-4 h-4 mr-2" /> Submit</>
                }
              </Button>
            </div>
          </>
        )}
      </div>
    </OnboardingLayout>
  );
};

export default ConsentSign;
